import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';
import { AuthService } from '../core/services/auth.service';
import { UnifiedShipment } from '../core/models/unified-shipment.models';
import { UserProfile } from '../core/models/user-profile';

/**
 * 🔐 SHIPMENT PERMISSIONS SERVICE
 * =================================
 *
 * Service centralisé pour gérer les permissions sur les shipments
 *
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ShipmentPermissionsService {

  constructor(private authService: AuthService) {}

  canEdit(shipment: UnifiedShipment): Observable<boolean> {
    return this.getCurrentUserProfile().pipe(
      map(user => {
        if (!user) return false;
        if (user.ability === 'topManager' || user.ability === 'manager') return true;
        return shipment.userId === user.uid;
      }),
      catchError(() => of(false))
    );
  }

  canDelete(shipment: UnifiedShipment): Observable<boolean> {
    return this.getCurrentUserProfile().pipe(
      map(user => user?.ability === 'topManager'),
      catchError(() => of(false))
    );
  }

  isAdmin(): Observable<boolean> {
    return this.getCurrentUserProfile().pipe(
      map(user => user?.ability === 'manager' || user?.ability === 'topManager'),
      catchError(() => of(false))
    );
  }

  // Lit `users/{uid}` (même source que ManagerGuard qui gate ce composant).
  // Lire `employees/{uid}` cassait les comptes legacy admin présents dans
  // `users/` mais absents de `employees/` en prod (ex. emails non `@befret.be`).
  // Voir .claude/rules/angular-frontoffice.md "Système legacy & source de vérité admin".
  private getCurrentUserProfile(): Observable<UserProfile | null> {
    return this.authService.getUserProfileObservable().pipe(
      take(1),
      catchError(() => of(null))
    );
  }
}
